import { Alert, Link } from '@mm/components';
import LandingPageCRUDActions from '@mm/state/actions/LandingPageCRUDActions';
import { unselectLandingPage } from '@mm/state/actions/viewActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import { compose, lifecycle, mapProps, withStateHandlers } from 'recompose';
import { InactiveBotAlert } from '../../../../components/InactiveBotAlert/InactiveBotAlert';
import { InactiveChannelAlertSmart } from '../../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import {
  ALIGNMENT_TYPES,
  DEFAULT_BG_COLOR,
  MSG_BUTTON_TYPES,
} from '../../constants/constants';
import LandingPagePreview from '../LandingPagePreview/LandingPagePreview';
import LandingPagePropertiesSideBar from '../LandingPagePropertiesSideBar/LandingPagePropertiesSideBar';
import './LandingPageDetails.scss';

const LandingPage = props => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const [alertVisible, setAlertVisible] = useState(!!facebookPage?.euLimitsAt);
  const onCloseAlert = () => {
    setAlertVisible(false);
  };
  return (
    <div className="landing-pages-panel__main-container">
      <InactiveChannelAlertSmart facebook type="Landing Page" />

      <Container fluid className="landing-page-details__outer-container">
        <Row className="landing-page-details">
          <Col
            className="landing-page-details__builder-content"
            xs="12"
            sm="9"
            md="9"
            style={{ backgroundColor: props.formBgColor }}
          >
            <NeedHelpLabel link="https://customers.ai/help/article/ltmot4wrsr-landing-pages" />
            {alertVisible && (
              <Alert
                type="warning"
                className="landing-page-details__warning-alert"
                closeIconProps={{ onClick: onCloseAlert }}
              >
                This feature is no longer available on Messenger due to{' '}
                <Link
                  href="https://customers.ai/help/article/cg1qvuexfy-updated-messaging-policies-for-eu-pages-users"
                  isBlank
                >
                  EU policy changes.
                </Link>
              </Alert>
            )}
            <InactiveBotAlert />
            <LandingPagePreview {...props} />
          </Col>
          <Col xs="12" sm="3" md="3" className="landing-page-details__col">
            <LandingPagePropertiesSideBar {...props} />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

// TODO: Current implementation of State management for Landing Pages
// is no longer a good choice. The code for this functionality
// became to big, as well as nestedness of passing local state and
// state modification functions we are using here.
// We should move out LP state to Redux with next mid-sized update to LP code.
const emptyInitialState = {
  showDetailsTab: true,
  formName: 'Untitled Landing Page 1',
  formLinkSlug: '',
  formPageId: '',
  formLink: '',
  landingBaseUrl: '',
  formLinkSlugError: '',
  formPageHeading: '',
  formPageText: '',
  formBgColor: DEFAULT_BG_COLOR,
  formImageUrl: '',
  formImageNewUrl: '',
  formTextAlignment: ALIGNMENT_TYPES.CENTER,
  formMsgButtonText: MSG_BUTTON_TYPES.LEARN_MORE_IN_MESSENGER,
};

const populateStatePropsFromSelectedEntity = props => {
  const {
    name,
    landingBaseUrl,
    slug,
    pageId,
    heading,
    text,
    color,
    imageUrl,
    textAlignment,
    msgButtonText,
  } = props.selectedEntity;
  return {
    ...emptyInitialState,
    formName: name,
    landingBaseUrl,
    formLinkSlug: slug,
    formPageId: pageId,
    formPageHeading: heading,
    formPageText: text,
    formImageUrl: imageUrl,
    formBgColor: color === null ? DEFAULT_BG_COLOR : color,
    formTextAlignment: textAlignment || ALIGNMENT_TYPES.CENTER,
    formMsgButtonText:
      msgButtonText || MSG_BUTTON_TYPES.LEARN_MORE_IN_MESSENGER,
  };
};

const initialState = props =>
  !props.selectedEntity
    ? emptyInitialState
    : populateStatePropsFromSelectedEntity(props);

const stateHandlers = {
  toggleShownTab: state => () => ({ showDetailsTab: !state.showDetailsTab }),
  updateFormName: () => value => {
    const noEmptyStringRegex = /^\s*$/;
    return { formName: value.replace(noEmptyStringRegex, '') };
  },
  updateFormLinkSlug: () => value => ({ formLinkSlug: value }),
  updateFormLinkSlugError: () => value => ({ formLinkSlugError: value }),
  updateFormPageId: () => value => ({ formPageId: value }),
  updateFormPageHeading: () => value => ({ formPageHeading: value }),
  updateFormPageText: () => value => ({ formPageText: value }),
  updateFormBgColor: () => value => ({ formBgColor: value }),
  setFormImageUrl: () => value => ({ formImageUrl: value }),
  setFormImageNewUrl: () => value => ({ formImageNewUrl: value }),
  setFormTextAlignment: () => value => ({ formTextAlignment: value }),
  setMsgButtonText: () => value => ({ formMsgButtonText: value }),
};

const mapStateToProps = state => ({
  selectedEntity: state.view.chatbotEditor.selectedLandingPage,
});

const propsMapper = ownerProps => {
  const {
    formPageHeading,
    updateFormPageHeading,
    formPageText,
    updateFormPageText,
    selectedEntity,
    formPageId,
    formBgColor,
    updateFormBgColor,
    formImageUrl,
    setFormImageUrl,
    formImageNewUrl,
    setFormImageNewUrl,
    formTextAlignment,
    setFormTextAlignment,
    formMsgButtonText,
    setMsgButtonText,
    ...restProps
  } = ownerProps;

  const LandingPageDesignProps = {
    selectedEntity,
    formPageHeading,
    updateFormPageHeading,
    formPageText,
    updateFormPageText,
    formPageId,
    formBgColor,
    updateFormBgColor,
    formImageUrl,
    setFormImageUrl,
    formImageNewUrl,
    setFormImageNewUrl,
    formTextAlignment,
    setFormTextAlignment,
    formMsgButtonText,
    setMsgButtonText,
  };

  return {
    LandingPageDesignProps,
    selectedEntity,
    formPageHeading,
    formPageText,
    updateFormPageHeading,
    updateFormPageText,
    formPageId,
    formBgColor,
    updateFormBgColor,
    formImageUrl,
    setFormImageUrl,
    formImageNewUrl,
    setFormImageNewUrl,
    formTextAlignment,
    setFormTextAlignment,
    formMsgButtonText,
    setMsgButtonText,
    ...restProps,
  };
};

function componentDidMount() {
  const { location, match, getLandingPageById } = this.props;
  const { pathname } = location;
  const {
    params: { chatbotId },
  } = match;
  const landingPageId = pathname.split('/').pop();
  getLandingPageById(chatbotId, landingPageId);
}

function componentWillReceiveProps(nextProps) {
  const { selectedEntity } = nextProps;
  if (selectedEntity && selectedEntity !== this.props.selectedEntity) {
    const {
      updateFormName,
      updateFormLinkSlug,
      updateFormPageId,
      updateFormPageHeading,
      updateFormPageText,
      updateFormBgColor,
      setFormImageUrl,
      setFormTextAlignment,
      setMsgButtonText,
    } = nextProps;
    const {
      name,
      slug,
      pageId,
      heading,
      text,
      color,
      imageUrl,
      textAlignment,
      msgButtonText,
    } = selectedEntity;

    updateFormName(name);
    updateFormLinkSlug(slug);
    updateFormPageId(pageId);
    updateFormPageHeading(heading);
    updateFormPageText(text);
    updateFormBgColor(color === null ? DEFAULT_BG_COLOR : color);
    setFormImageUrl(imageUrl);
    setFormTextAlignment(textAlignment || ALIGNMENT_TYPES.CENTER);
    setMsgButtonText(msgButtonText || MSG_BUTTON_TYPES.LEARN_MORE_IN_MESSENGER);
  }
}

function componentWillUnmount() {
  this.props.deselectLandingPage();
}

const mapDispatchToProps = dispatch => ({
  deselectLandingPage: () => dispatch(unselectLandingPage()),
  getLandingPageById: (botId, landingPageId) =>
    dispatch(LandingPageCRUDActions.retrieveById(botId, landingPageId)),
});

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  // I need to lift state up to this level, since it is
  // shared by several children.
  withStateHandlers(initialState, stateHandlers),
  mapProps(propsMapper),
  lifecycle({
    componentWillReceiveProps,
    componentWillUnmount,
    componentDidMount,
  }),
);

export const LandingPageDetails = enhance(LandingPage);
